import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  createPublication,
  deletePublication,
  getPublication,
  listPublications,
  updatePublication,
} from './api';
import type { PublicationFilters, PublicationPatch } from './types';

function errMsg(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

export const publicationKeys = {
  all: ['publications'] as const,
  list: (filters?: PublicationFilters) => [...publicationKeys.all, 'list', filters] as const,
  detail: (id: string) => [...publicationKeys.all, 'detail', id] as const,
};

export function usePublications(filters: PublicationFilters = {}) {
  return useQuery({
    queryKey: publicationKeys.list(filters),
    queryFn: () => listPublications(filters),
  });
}

export function usePublication(id: string | null) {
  return useQuery({
    queryKey: publicationKeys.detail(id ?? ''),
    queryFn: () => getPublication(id as string),
    enabled: !!id,
  });
}

export function useCreatePublication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PublicationPatch) => createPublication(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: publicationKeys.all });
      toast.success('Publication created');
    },
    onError: (err) => toast.error('Failed to create publication', { description: errMsg(err) }),
  });
}

export function useUpdatePublication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PublicationPatch }) => updatePublication(id, data),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: publicationKeys.all });
      qc.invalidateQueries({ queryKey: publicationKeys.detail(vars.id) });
    },
    onError: (err) => toast.error('Failed to update publication', { description: errMsg(err) }),
  });
}

export function useDeletePublication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePublication(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: publicationKeys.all });
      toast.success('Publication deleted');
    },
    onError: (err) => toast.error('Failed to delete publication', { description: errMsg(err) }),
  });
}
